import { MAX_LOOP_COUNT } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import React, { FC, useRef } from 'react'
import { useTreeState } from '../../../stores/Root'
import { Box, HorizontalStyledScrollable, Row } from '../../core/Components'
import { CloseIcon } from '../../core/icons/icons'
import { gray8 } from '../../platform/color'
import { LOOP_LETTERS } from '../../studio/loops/InstrumentLoop'
import { MidiVisualizer } from '../../studio/loops/LoopNoteVisualizer'
import { LOOP_HEIGHT } from './LoopSelector'

const WIDTH_PX = 120
const WIDTH = `${WIDTH_PX}px`
const LETTER_HEIGHT = '12px'
const LETTER_MARGIN = '5px'

interface SelectLoopContainer {
  stemIndex: number
  loopIndex: number
  loopColor: string
  onClick: () => void
  onClickDelete: () => void
  style?: {}
  isSelected?: boolean
}

const SelectLoopContainer: FC<SelectLoopContainer> = observer(
  ({ stemIndex, loopIndex, loopColor, onClick, onClickDelete, style, isSelected }) => {
    const [isHover, setIsHover] = React.useState(false)
    const { studio } = useTreeState()
    return (
      <Row
        css={{
          backgroundColor: loopColor,
          minWidth: WIDTH,
          maxWidth: WIDTH,
          height: LOOP_HEIGHT,
          borderStyle: 'solid',
          borderColor: isSelected ? '#585FFC' : '#E3E3E3',
          borderWidth: isSelected ? '2px' : '1px',
          top: isSelected && '-1px',
          right: isSelected && '-1px',
          bottom: isSelected && '-1px',
          left: isSelected && '-1px',
          borderRadius: '8px',
          color: 'var(--studio-off-white)',
          transition: '150ms',
          opacity: isHover && '80%',
          position: 'relative',
          cursor: 'pointer',
          ...style
        }}
        onClick={onClick}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        {loopIndex > -1 ? (
          <>
            <Box
              style={{
                fontSize: LETTER_HEIGHT,
                lineHeight: LETTER_HEIGHT,
                color: gray8,
                paddingLeft: '5px',
                paddingTop: '5px'
              }}
            >
              {LOOP_LETTERS[loopIndex]}
            </Box>
            <MidiVisualizer
              stem={studio.song.stems[stemIndex]}
              loopNumber={loopIndex}
              style={{
                height: `calc(${LOOP_HEIGHT} - ${LETTER_HEIGHT} - 2 * ${LETTER_MARGIN})`,
                width: WIDTH,
                paddingTop: `calc(${LETTER_HEIGHT} + 2 * ${LETTER_MARGIN})`
              }}
              fill={gray8}
            />
          </>
        ) : (
          <Row css={{ flex: '1', height: '100%' }} verticalCenter horizontalCenter>
            <img src="/static/icons/add_muted_24dp.svg" style={{ height: '50%' }} />
          </Row>
        )}
        {isHover && onClickDelete && (
          <Box unselectable>
            <CloseIcon
              style={{
                height: '14px',
                width: '14px',
                zIndex: '3',
                position: 'absolute',
                right: '5px',
                top: '5px',
                cursor: 'pointer',
                color: gray8
              }}
              onClick={(e) => {
                e.stopPropagation()
                onClickDelete()
              }}
            />
          </Box>
        )}
      </Row>
    )
  }
)

const EmptySelectedContainer: FC<any> = ({ stemIndex, loopColor }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { studio } = useTreeState()
  const stem = studio.song.stems[stemIndex]
  const loops = stem.loops
  const currentLoopIndex = stem.getLoopIndex(studio.selectedSection) - 1

  return (
    <HorizontalStyledScrollable
      css={{
        display: 'flex',
        minWidth: '100%',
        height: `calc(${LOOP_HEIGHT} + 10px)`,
        paddingBottom: '5px',
        overflowY: 'visible'
      }}
      ref={ref}
    >
      {loops.length < MAX_LOOP_COUNT && (
        <SelectLoopContainer
          loopIndex={-1}
          loopColor="transparent"
          onClick={() => {
            studio.song.addLoopToStem(stemIndex)
            studio.song.updateLoopForStem(stemIndex, studio.selectedSection, loops.length)
          }}
          onClickDelete={undefined}
          stemIndex={stemIndex}
          style={{ marginRight: '10px' }}
        />
      )}
      {loops.map((loop, index) => (
        <SelectLoopContainer
          loopIndex={index}
          loopColor={loopColor}
          onClick={() => {
            studio.song.updateLoopForStem(stemIndex, studio.selectedSection, index + 1)
          }}
          onClickDelete={() => {
            studio.song.stems[stemIndex].removeLoop(index)
          }}
          key={index}
          stemIndex={stemIndex}
          isSelected={currentLoopIndex === index}
          style={{ marginRight: '10px' }}
        />
      ))}
    </HorizontalStyledScrollable>
  )
}

export default observer(EmptySelectedContainer)
